import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Gift, Check, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { REFERRAL_DISCOUNT } from '@/config/earnings';

// ─── A referral code, for every plan at once ──────────────────────────────────
// The box people look for on a pricing page. A valid code reprices every plan
// card, and checkout opens with it already applied. The pricing page used to
// say "enter it at checkout", and in checkout the box was folded away behind
// a link, so people who had a code could not find where it went.
//
// Works signed out: the server checks only that the code exists (the
// checkout, which is always signed in, checks it is not the buyer's own). The
// code is kept for the browser tab, so it survives signing in.

const API_URL = import.meta.env.VITE_API_URL ?? '';
export const CHECKOUT_REF_KEY = 'bl_checkout_ref';
const LINK_REF_KEY = 'bl_ref';   // a ?ref= link (src/lib/referral.js)

function readStorage(store, key) {
  try { return store.getItem(key) || ''; } catch { return ''; }
}
function writeStorage(store, key, value) {
  try { if (value) store.setItem(key, value); else store.removeItem(key); } catch { /* this tab only */ }
}

/**
 * @param {object} props
 * @param {{code:string, referrerName?:string, discountRate:number}|null} props.applied
 * @param {(applied: object|null) => void} props.onApplied
 */
export default function ReferralBar({ applied, onApplied }) {
  const { session } = useAuth();
  const [code, setCode] = useState('');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const prefilled = useRef(false);

  const apply = useCallback(async (value, { quiet = false } = {}) => {
    const clean = String(value || '').trim();
    if (!clean) return;
    setChecking(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/billing/referral/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ code: clean }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.valid) {
        // A code filled in for them that no longer works is dropped quietly;
        // one they typed is explained.
        if (!quiet) setError(data.error || 'That code could not be applied.');
        writeStorage(sessionStorage, CHECKOUT_REF_KEY, '');
        onApplied(null);
        return;
      }
      setCode(data.code);
      writeStorage(sessionStorage, CHECKOUT_REF_KEY, data.code);
      onApplied({ code: data.code, referrerName: data.referrerName || null, discountRate: data.discountRate });
    } catch {
      if (!quiet) setError('Could not check that code. Try again.');
    } finally {
      setChecking(false);
    }
  }, [session, onApplied]);

  // A code from a ?ref= link, or one applied earlier in this tab, or the code
  // this customer was referred with: filled in and applied for them.
  useEffect(() => {
    if (prefilled.current || applied) return;
    prefilled.current = true;
    (async () => {
      let found = '';
      try { found = new URLSearchParams(window.location.search).get('ref') || ''; } catch { /* no URL */ }
      found = found || readStorage(sessionStorage, CHECKOUT_REF_KEY) || readStorage(localStorage, LINK_REF_KEY);
      if (!found && session?.access_token) {
        try {
          const res = await fetch(`${API_URL}/api/billing/referral/suggested`, {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          if (res.ok) found = (await res.json())?.code || '';
        } catch { /* typed by hand instead */ }
      }
      if (found) { setCode(found); apply(found, { quiet: true }); }
    })();
  }, [applied, session, apply]);

  const remove = () => {
    writeStorage(sessionStorage, CHECKOUT_REF_KEY, '');
    setCode('');
    setError('');
    onApplied(null);
  };

  return (
    <div className="max-w-2xl mx-auto mb-8 rounded-2xl border border-orange-500/25 bg-orange-500/[0.07] p-4 sm:p-5">
      {applied ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-2.5 text-sm text-emerald-300 min-w-0">
            <span className="w-8 h-8 rounded-full bg-emerald-500/15 grid place-items-center shrink-0"><Check className="w-4 h-4" /></span>
            <span>
              <span className="font-mono font-bold text-white">{applied.code}</span> applied: {Math.round(applied.discountRate * 100)}% off every plan
              {applied.referrerName ? <span className="text-emerald-400/70">, from {applied.referrerName}</span> : null}
            </span>
          </p>
          <button type="button" onClick={remove} className="text-xs font-medium text-zinc-400 hover:text-white">Remove</button>
        </div>
      ) : (
        <form
          onSubmit={(e) => { e.preventDefault(); apply(code); }}
          className="flex flex-col sm:flex-row sm:items-center gap-3"
        >
          <label htmlFor="pricing-referral-code" className="flex items-center gap-2.5 text-sm text-orange-100 sm:flex-1 min-w-0">
            <span className="w-8 h-8 rounded-full bg-orange-500/15 grid place-items-center shrink-0"><Gift className="w-4 h-4 text-orange-400" /></span>
            <span>Have a referral code? Get <strong className="text-white">{REFERRAL_DISCOUNT} off</strong> any plan.</span>
          </label>
          <div className="flex gap-2 sm:w-[280px] shrink-0">
            <input
              id="pricing-referral-code"
              value={code}
              onChange={(e) => { setCode(e.target.value.replace(/\s+/g, '')); setError(''); }}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              className="flex-1 min-w-0 h-10 px-3 rounded-xl border border-zinc-700 bg-zinc-950 text-sm font-mono text-white placeholder-zinc-600 outline-none focus:border-orange-500"
            />
            <button
              type="submit"
              disabled={checking || !code.trim()}
              className="h-10 px-4 rounded-xl bg-orange-500 hover:bg-orange-400 text-ink text-sm font-bold disabled:opacity-50"
            >
              {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
            </button>
          </div>
        </form>
      )}
      {error && <p role="alert" className="mt-2 text-xs text-red-400 sm:text-right">{error}</p>}
    </div>
  );
}
